import { openDB, DBSchema } from "idb"

export type SpeciesProfile = {
  id: string // Primary key: species_id or generated
  ala_guid: string | null // ALA taxonConceptID (null for local-only species)
  projectId: string
  source: "fieldbook" | "ala"
  name: string
  kingdom: "Plantae" | "Animalia" | null
  commonName?: string
  family?: string
  stateConservation?: string
  imageCount: number // Total ALA images downloaded
  timestamp: number

  // Local Supabase data (optional, only present if species observed locally)
  localData?: {
    observationCount: number // # of observations across all projects
    projectCount: number // # of unique projects with observations
    localPhotoCount: number // # of photos from local observations
  }
}

export type SpeciesImage = {
  id: string // either fieldbook image Id or ALA image Id
  speciesId: string // References SpeciesProfile.id
  full: Blob
  thumbnail: Blob
  timestamp: number

  // Track image source
  source: "ala" | "local" // Distinguish ALA vs observation photos
  sourceId?: string // photo_id if local
}

interface SpeciesProfileDB extends DBSchema {
  profiles: {
    key: string
    value: SpeciesProfile
    indexes: {
      "by-project": string
      "by-project-id": [string, string]
      "by-ala-guid": string
      "by-project-species-name": [string, string]
      timestamp: number
    }
  }
  images: {
    key: string
    value: SpeciesImage
    indexes: {
      "by-species": string
      timestamp: number
    }
  }
}

// Initialize the DB
export const speciesProfileDB = openDB<SpeciesProfileDB>(
  "species-profiles",
  1,
  {
    upgrade(db) {
      const profileStore = db.createObjectStore("profiles", { keyPath: "id" })
      profileStore.createIndex("by-project", "projectId")
      profileStore.createIndex("by-project-species-name", ["projectId", "name"])
      profileStore.createIndex("by-project-id", ["projectId", "id"])
      profileStore.createIndex("by-ala-guid", "ala_guid")
      profileStore.createIndex("timestamp", "timestamp")

      const imageStore = db.createObjectStore("images", { keyPath: "id" })
      imageStore.createIndex("by-species", "speciesId")
      imageStore.createIndex("timestamp", "timestamp")
    },
  },
)

// Helper function to add timeout to operations
const withTimeout = <T>(promise: Promise<T>, timeoutMs = 2000): Promise<T> => {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error("IndexedDB operation timed out"))
    }, timeoutMs)

    promise
      .then((result) => {
        clearTimeout(timeoutId)
        resolve(result)
      })
      .catch((error) => {
        clearTimeout(timeoutId)
        reject(error)
      })
  })
}

// Profile operations
export const putProfile = async (profile: SpeciesProfile): Promise<void> => {
  const db = await speciesProfileDB
  return withTimeout(db.put("profiles", profile).then(() => {}))
}

// Batch version for better performance during sync
export const putProfileBatch = async (
  profiles: SpeciesProfile[],
): Promise<void> => {
  const db = await speciesProfileDB
  const tx = db.transaction("profiles", "readwrite")

  await Promise.all(profiles.map((profile) => tx.store.put(profile)))

  await tx.done
}

export const getProfile = async (
  id: string,
): Promise<SpeciesProfile | undefined> => {
  const db = await speciesProfileDB
  return withTimeout(db.get("profiles", id))
}

export const getProfilesByProject = async (
  projectId: string,
): Promise<SpeciesProfile[]> => {
  const db = await speciesProfileDB
  const tx = db.transaction("profiles", "readonly")
  const index = tx.store.index("by-project-species-name")

  // Use IDBKeyRange to get all entries where the first key (projectId) matches
  const range = IDBKeyRange.bound([projectId, ""], [projectId, "\uffff"])

  return withTimeout(index.getAll(range))
}

export const deleteProfilesByProject = async (
  projectId: string,
): Promise<void> => {
  const profiles = await getProfilesByProject(projectId)
  const db = await speciesProfileDB
  const tx = db.transaction(["profiles", "images"], "readwrite")

  // Delete all profiles for this project
  await Promise.all(
    profiles.map((profile) => tx.objectStore("profiles").delete(profile.id)),
  )

  // Delete all images for species in this project
  await Promise.all(
    profiles.map(async (profile) => {
      const imageIndex = tx.objectStore("images").index("by-species")
      const keys = await imageIndex.getAllKeys(profile.id)
      return await Promise.all(
        keys.map((key) => tx.objectStore("images").delete(key)),
      )
    }),
  )

  await tx.done
}

// Image operations
async function generateThumbnail(blob: Blob, maxSize = 200): Promise<Blob> {
  const img = await createImageBitmap(blob)
  const scale = Math.min(maxSize / img.width, maxSize / img.height, 1)

  const canvas = new OffscreenCanvas(img.width * scale, img.height * scale)
  const ctx = canvas.getContext("2d")!
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

  return canvas.convertToBlob({ type: "image/jpeg", quality: 0.7 })
}

export const putImage = async (
  id: string,
  speciesId: string,
  imageBlob: Blob,
  source: "ala" | "local" = "ala",
  sourceId?: string,
): Promise<void> => {
  const thumbnail = await generateThumbnail(imageBlob)

  const db = await speciesProfileDB
  return withTimeout(
    db
      .put("images", {
        id,
        speciesId,
        full: imageBlob,
        thumbnail,
        timestamp: Date.now(),
        source,
        sourceId,
      })
      .then(() => {}),
  )
}

// Batch version for better performance during sync
export const putImageBatch = async (
  images: Array<{
    id: string
    speciesId: string
    imageBlob: Blob
    source: "ala" | "local"
    sourceId?: string
  }>,
): Promise<void> => {
  // Generate all thumbnails BEFORE opening the transaction
  // This prevents transaction timeout issues
  const imagesWithThumbnails = await Promise.all(
    images.map(async ({ id, speciesId, imageBlob, source, sourceId }) => {
      const thumbnail = await generateThumbnail(imageBlob)

      return {
        id,
        speciesId,
        full: imageBlob,
        thumbnail,
        timestamp: Date.now(),
        source,
        sourceId,
      }
    }),
  )

  // Now open transaction and do all puts synchronously
  const db = await speciesProfileDB
  const tx = db.transaction("images", "readwrite")

  // Put all images in the transaction (no awaiting inside)
  for (const image of imagesWithThumbnails) {
    tx.store.put(image)
  }

  await tx.done
}

export const getImage = async (
  id: string,
): Promise<SpeciesImage | undefined> => {
  const db = await speciesProfileDB
  return withTimeout(db.get("images", id))
}

export const getImages = async (ids: string[]): Promise<SpeciesImage[]> => {
  const db = await speciesProfileDB
  const tx = db.transaction("images", "readonly")

  const results = await Promise.all(
    ids.map((id) => withTimeout(tx.store.get(id))),
  )

  return results.filter((img): img is SpeciesImage => img !== undefined)
}

export const getAllImagesForSpecies = async (
  speciesId: string,
): Promise<SpeciesImage[]> => {
  const db = await speciesProfileDB
  const tx = db.transaction("images", "readonly")
  const index = tx.store.index("by-species")
  return withTimeout(index.getAll(speciesId))
}

export const getFirstImageForSpecies = async (
  speciesId: string,
): Promise<SpeciesImage | undefined> => {
  const db = await speciesProfileDB
  const tx = db.transaction("images", "readonly")
  const index = tx.store.index("by-species")
  return withTimeout(index.get(speciesId))
}

export const downloadImage = async (url: string): Promise<Blob> => {
  const response = await fetch(url)
  if (!response.ok) throw new Error(`Failed to fetch image: ${response.status}`)
  return response.blob()
}

const MAX_PROMISE_CACHE_SIZE = 20

const promiseCache = new Map<
  string,
  ReturnType<typeof getAllImagesForSpecies>
>()

/**
 * A wrapper around getImageBlobs allowing use in React Suspense
 * @param speciesId
 * @returns
 */
export function getSuspenseImages(speciesId: string, refreshKey: number = 0) {
  const cacheKey = `${speciesId}-${refreshKey}`

  if (!promiseCache.has(cacheKey)) {
    // Clear old entries for this species
    Array.from(promiseCache.keys())
      .filter((key) => key.startsWith(`${speciesId}-`))
      .forEach((key) => promiseCache.delete(key))

    // Evict oldest entries if cache is full
    while (promiseCache.size >= MAX_PROMISE_CACHE_SIZE) {
      const oldestKey = promiseCache.keys().next().value
      if (oldestKey) promiseCache.delete(oldestKey)
    }

    promiseCache.set(cacheKey, getAllImagesForSpecies(speciesId))
  }

  return promiseCache.get(cacheKey)!
}
